/**
 * AppContext.tsx
 *
 * CACHING UPDATE:
 * - Switched from sessionStorage to localStorage cache (persists across app restarts)
 * - Stale-while-revalidate: if cache is 10-60 min old, show stale data instantly
 *   then silently fetch fresh data and update in background
 * - Pull-to-refresh still forces a fresh fetch and shows loading state
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import type { Venue, UserLocation } from '../types'
import { getVenues } from '../services/venueService'
import { cachedGetVenues, invalidateVenueCache } from '../utils/venueCache'
import { useFavorites, type UseFavoritesReturn } from '../hooks/useFavorites'
import { Analytics, initAnalytics, rotateAnalyticsSession } from '../services/analytics'
import { initPush } from '../services/pushNotifications'
import { resetImpressionSession } from '../services/impressionBuffer'

interface AppContextValue {
  venues: Venue[]
  loading: boolean
  error: string | null
  refetchVenues: () => void

  userLocation: UserLocation | null
  requestLocation: () => void
  locationPermission: 'unknown' | 'granted' | 'denied'

  favorites: UseFavoritesReturn

  city: string
  setCity: (c: string) => void
}

const AppContext = createContext<AppContextValue | null>(null)

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [venues, setVenues]                   = useState<Venue[]>([])
  const [loading, setLoading]                 = useState(true)
  const [error, setError]                     = useState<string | null>(null)
  const [userLocation, setUserLocation]       = useState<UserLocation | null>(null)
  const [locationPermission, setLocationPermission] = useState<'unknown' | 'granted' | 'denied'>('unknown')
  const [city, setCity]                       = useState('Cincinnati')

  const favorites = useFavorites()

  useEffect(() => {
    initAnalytics()
    initPush()
    Analytics.appOpened()
  }, [])

  const fetchVenues = useCallback(async (forceRefresh = false, silent = false) => {
    // silent=true refreshes data without toggling the loading state —
    // used on app resume, where a spinner flash on every app-switch
    // would make the app feel broken rather than fresh.
    if (!silent) { setLoading(true); setError(null) }
    try {
      const data = await cachedGetVenues(
        city,
        getVenues,
        forceRefresh,
        // onRefresh: called when background revalidation completes.
        // Updates venues silently without showing a loading state.
        (fresh) => { setVenues(fresh as Venue[]) }
      )
      setVenues(data as Venue[])
      if (silent) setError(null)
    } catch (e: any) {
      if (!silent) setError(e.message)
    }
    if (!silent) setLoading(false)
  }, [city])

  // INITIAL LOAD vs CITY CHANGE:
  // - First mount: fetchVenues(false) → uses the localStorage cache.
  //   Fresh cache (<10 min) renders instantly with zero network wait.
  //   Stale cache (10–60 min) renders instantly, then revalidates in
  //   the background via onRefresh. This is the entire point of
  //   venueCache.ts — passing true here made the cache dead code and
  //   forced a "Loading deals..." spinner on every cold start.
  // - City change: forces a fresh fetch (different dataset entirely).
  const isFirstLoad = useRef(true)
  useEffect(() => {
    const force = !isFirstLoad.current
    isFirstLoad.current = false
    fetchVenues(force)
  }, [fetchVenues])

  // ── LIVE STATUS TICK ────────────────────────────────────────────────────
  // "Live Now" / "ends in 20m" badges are computed at render time — nothing
  // re-renders as time passes, so a phone left open showed frozen statuses.
  // Refreshing the array identity once per minute makes every memoized
  // card/hero recompute. 60 venues → negligible cost; revisit at 1000+.
  useEffect(() => {
    const id = setInterval(() => {
      setVenues(v => (v.length ? [...v] : v))
    }, 60_000)
    return () => clearInterval(id)
  }, [])

  // ── RESUME HANDLING ─────────────────────────────────────────────────────
  // When the app returns to the foreground:
  //  1. Silently refresh venues through the cache (fresh <10min = instant,
  //     stale = instant + background revalidate). No spinner.
  //  2. If backgrounded >30 min, rotate the analytics session + impression
  //     dedup — native JS processes survive for days, and without this a
  //     user's Tuesday and Thursday visits counted as one session.
  // Both web (visibilitychange) and native (appStateChange) paths are wired;
  // on native both may fire, so refreshes are throttled to one per 30s.
  const lastHiddenAt = useRef<number | null>(null)
  const lastResumeRefresh = useRef(0)

  useEffect(() => {
    const SESSION_GAP_MS = 30 * 60 * 1000
    const RESUME_THROTTLE_MS = 30 * 1000

    const handleResume = () => {
      const now = Date.now()
      if (now - lastResumeRefresh.current < RESUME_THROTTLE_MS) return
      lastResumeRefresh.current = now

      const gap = lastHiddenAt.current ? now - lastHiddenAt.current : 0
      if (gap > SESSION_GAP_MS) {
        rotateAnalyticsSession()
        resetImpressionSession()
        Analytics.appResumed()
      }
      fetchVenues(false, true)  // cache-aware, silent
    }

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        lastHiddenAt.current = Date.now()
      } else {
        handleResume()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)

    let removeNative: (() => void) | undefined
    if ((window as any).Capacitor?.isNativePlatform?.()) {
      import('@capacitor/app').then(({ App }) => {
        App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) handleResume()
          else lastHiddenAt.current = Date.now()
        }).then(handle => { removeNative = () => handle.remove() })
      })
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      removeNative?.()
    }
  }, [fetchVenues])

  // Pull-to-refresh — always forces fresh fetch + shows loading
  const refetchVenues = useCallback(() => {
    invalidateVenueCache(city)
    fetchVenues(true)
  }, [fetchVenues, city])

  const requestLocation = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setLocationPermission('denied')
      return
    }
    navigator.geolocation.getCurrentPosition(
      pos => {
        setUserLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          source: 'gps',
          label: 'Your location',
        })
        setLocationPermission('granted')
      },
      () => setLocationPermission('denied')
    )
  }, [])

  return (
    <AppContext.Provider value={{
      venues, loading, error, refetchVenues,
      userLocation, requestLocation, locationPermission,
      favorites,
      city, setCity,
    }}>
      {children}
    </AppContext.Provider>
  )
}

export function useAppContext(): AppContextValue {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error('useAppContext must be used within AppProvider')
  return ctx
}
